import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconDashboard = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 3v8h8V3h-8zm0 18h8v-8h-8v8zM3 21h8v-8H3v8zm0-10h8V3H3v8z"/>
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);
const IconServer = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <rect x="2" y="2" width="20" height="8" rx="2"/>
    <rect x="2" y="14" width="20" height="8" rx="2"/>
    <circle cx="6" cy="6" r="1" fill="currentColor" stroke="none"/>
    <circle cx="6" cy="18" r="1" fill="currentColor" stroke="none"/>
  </svg>
);
const IconPolicy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);
const IconKey = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <circle cx="7.5" cy="15.5" r="5.5"/>
    <path d="m21 2-9.6 9.6"/>
    <path d="m15.5 7.5 3 3L22 7l-3-3"/>
  </svg>
);
const IconFlag = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
    <line x1="4" y1="22" x2="4" y2="15"/>
  </svg>
);
const IconLogout = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" x2="9" y1="12" y2="12"/>
  </svg>
);

// ─── Nav item ─────────────────────────────────────────────────────────────────
interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, end }) => (
  <NavLink
    to={to}
    end={end}
    style={({ isActive }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 12px',
      margin: '1px 6px',
      borderRadius: '4px',
      textDecoration: 'none',
      fontSize: '13px',
      fontWeight: isActive ? 600 : 400,
      color: isActive ? '#FFFFFF' : 'var(--sidebar-text)',
      background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
      transition: 'background .1s, color .1s',
      userSelect: 'none' as const,
    })}
    onMouseEnter={(e) => {
      if (e.currentTarget.getAttribute('aria-current') !== 'page') {
        e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
        e.currentTarget.style.color = '#FFFFFF';
      }
    }}
    onMouseLeave={(e) => {
      if (e.currentTarget.getAttribute('aria-current') !== 'page') {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'var(--sidebar-text)';
      }
    }}
  >
    <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</span>
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
  </NavLink>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initials = user ? user.username.slice(0, 2).toUpperCase() : '??';

  return (
    <aside style={{
      width: 'var(--sidebar-w)',
      minWidth: 'var(--sidebar-w)',
      height: '100vh',
      background: 'var(--sidebar-bg)',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      position: 'sticky',
      top: 0,
      flexShrink: 0,
    }}>

      {/* ── Brand ── */}
      <div style={{
        padding: '0 14px',
        height: 48,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
      }}>
        <img
          src="https://tickets.falconsystem.com/assets/images/favicon.ico"
          alt="Falcon"
          width="26"
          height="26"
          style={{ objectFit: 'contain', flexShrink: 0, filter: 'brightness(0) invert(1)' }}
        />
        <div>
          <div style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '13.5px', lineHeight: 1.25 }}>Falcon</div>
          <div style={{ color: '#6E6E6E', fontSize: '10px', lineHeight: 1.2 }}>Email Extractor</div>
        </div>
      </div>

      {/* ── Navigation ── */}
      <nav style={{ flex: 1, paddingTop: '8px', overflowY: 'auto' }}>
        <div style={{
          padding: '8px 18px 4px',
          fontSize: '10px', fontWeight: 600, color: '#5E5E5E',
          textTransform: 'uppercase', letterSpacing: '.09em',
        }}>
          Main
        </div>
        <NavItem to="/"             end icon={<IconDashboard />} label="Dashboard"    />
        <NavItem to="/extractions"      icon={<IconMail />}     label="Extractions"  />
        <NavItem to="/mailboxes"        icon={<IconServer />}   label="Mailboxes"    />

        <div style={{
          padding: '14px 18px 4px',
          fontSize: '10px', fontWeight: 600, color: '#5E5E5E',
          textTransform: 'uppercase', letterSpacing: '.09em',
        }}>
          Manage
        </div>
        <NavItem to="/policies"      icon={<IconPolicy />} label="Policies"      />
        <NavItem to="/api-keys"      icon={<IconKey />}    label="API Keys"      />
        <NavItem to="/feature-flags" icon={<IconFlag />}   label="Feature Flags" />
      </nav>

      {/* ── User footer ── */}
      {user && (
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '8px 10px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0,
            letterSpacing: '.04em',
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#E0E0E0', fontSize: '12.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
              {user.username}
            </div>
            <div style={{ color: '#6E6E6E', fontSize: '10.5px', textTransform: 'capitalize', lineHeight: 1.2 }}>
              {user.role}
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            style={{
              background: 'transparent', border: 'none', color: '#6E6E6E',
              cursor: 'pointer', padding: '5px', borderRadius: '4px',
              display: 'flex', alignItems: 'center', flexShrink: 0,
              transition: 'color .1s, background .1s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#F1707B'; e.currentTarget.style.background = 'rgba(241,112,123,0.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6E6E6E'; e.currentTarget.style.background = 'transparent'; }}
          >
            <IconLogout />
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
